import React, { useMemo, useState, useCallback } from 'react';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceArea, Customized,
} from 'recharts';
import type { HeadToHeadEncounter } from '@/types/api';
import {
  buildYearBackgroundBands,
  buildYearSections,
  makeYearLabelRenderer,
  buildChartDateLabels,
  makeDateAxisTickRenderer,
} from '@/utils/historyChart';

// ---------------------------------------------------------------------------
// Colors — neutral line colors that pair with green/red win/loss dots
// ---------------------------------------------------------------------------
const PLAYER_A_COLOR = '#6366f1'; // indigo-500
const PLAYER_B_COLOR = '#475569'; // slate-600
const AREA_FILL = '#e2e8f0';      // slate-200
const WIN_DOT = '#10b981';        // emerald-500
const LOSS_DOT = '#ef4444';       // red-500

// ---------------------------------------------------------------------------
// Chart data row
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Chart data row — when showGameType=true, CSR values are split per game type
// so each game's line connects only its own points.
// ---------------------------------------------------------------------------
export interface ChartRow {
  /** Unique index within the encounter list (for hover matching across chart + sidebar). */
  matchIndex: number;
  date: string;
  displayDate: string;
  playerA_csr: number | null;
  playerB_csr: number | null;
  winnerId: string | null;
  playerA_race: number | null;
  playerB_race: number | null;
  playerA_gamesWon: number | null;
  playerB_gamesWon: number | null;
  venue: string | null;
  divisionName: string | null;
  isMakeup: boolean;
  gameType: string;
  sameDayIndex: number;
  sameDayCount: number;
  // Per-game-type CSR columns (populated when showGameType is true)
  a_8ball_csr?: number | null;
  b_8ball_csr?: number | null;
  a_9ball_csr?: number | null;
  b_9ball_csr?: number | null;
  a_10ball_csr?: number | null;
  b_10ball_csr?: number | null;
}

function formatDisplayDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatFullDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Map game type string to per-game-type CSR column key prefix */
const GAME_KEY_PREFIX: Record<string, string> = {
  '8-ball': '8ball',
  '9-ball': '9ball',
  '10-ball': '10ball',
};

export function buildChartData(encounters: HeadToHeadEncounter[], splitByGameType = false): ChartRow[] {
  // First pass: build rows
  const rows: ChartRow[] = encounters.map((e, idx) => {
    const dateKey = e.date.split('T')[0];
    const aCsr = e.player_a_csr != null ? Math.round(e.player_a_csr) : null;
    const bCsr = e.player_b_csr != null ? Math.round(e.player_b_csr) : null;

    const row: ChartRow = {
      matchIndex: idx,
      date: dateKey,
      displayDate: formatDisplayDate(dateKey),
      playerA_csr: aCsr,
      playerB_csr: bCsr,
      winnerId: e.winner_id,
      playerA_race: e.player_a_race,
      playerB_race: e.player_b_race,
      playerA_gamesWon: e.player_a_games_won,
      playerB_gamesWon: e.player_b_games_won,
      venue: e.venue,
      divisionName: e.division_name,
      isMakeup: e.is_makeup_match,
      gameType: e.game_type,
      sameDayIndex: 0,
      sameDayCount: 1,
    };

    // When splitting, put CSR into game-specific columns so each Line
    // connects only its own game type's points (others stay null).
    if (splitByGameType) {
      const gk = GAME_KEY_PREFIX[e.game_type];
      if (gk) {
        (row as any)[`a_${gk}_csr`] = aCsr;
        (row as any)[`b_${gk}_csr`] = bCsr;
      }
    }

    return row;
  });

  // Second pass: compute same-day clustering
  const dateCounts = new Map<string, number>();
  for (const r of rows) dateCounts.set(r.date, (dateCounts.get(r.date) ?? 0) + 1);
  const dateSeq = new Map<string, number>();
  for (const r of rows) {
    const count = dateCounts.get(r.date) ?? 1;
    const idx = dateSeq.get(r.date) ?? 0;
    r.sameDayCount = count;
    r.sameDayIndex = idx;
    dateSeq.set(r.date, idx + 1);
  }

  return rows;
}

// ---------------------------------------------------------------------------
// Per-game-type line styling (used in "All" mode)
// ---------------------------------------------------------------------------
const GAME_LINE_STYLES: { gameType: string; keyPrefix: string; dash: string; label: string }[] = [
  { gameType: '8-ball',  keyPrefix: '8ball',  dash: '',        label: '8-Ball' },
  { gameType: '9-ball',  keyPrefix: '9ball',  dash: '6 3',     label: '9-Ball' },
  { gameType: '10-ball', keyPrefix: '10ball', dash: '2 3',     label: '10-Ball' },
];

// ---------------------------------------------------------------------------
// Pool ball SVG dot — used when showGameType is true ("All" tab)
// ---------------------------------------------------------------------------
const BALL_COLORS: Record<string, { fill: string; text: string; label: string }> = {
  '8-ball':  { fill: '#1e1e1e', text: '#ffffff', label: '8' },
  '9-ball':  { fill: '#fbbf24', text: '#1e1e1e', label: '9' },
  '10-ball': { fill: '#3b82f6', text: '#ffffff', label: '10' },
};

function PoolBallDot({ cx, cy, gameType, isWinner, sameDayIndex, sameDayCount }: { cx: number; cy: number; gameType: string; isWinner: boolean; sameDayIndex: number; sameDayCount: number }) {
  const ball = BALL_COLORS[gameType] ?? BALL_COLORS['8-ball'];
  const r = 8;
  const haloColor = isWinner ? WIN_DOT : LOSS_DOT;
  const offsetX = sameDayCount > 1 ? (sameDayIndex - (sameDayCount - 1) / 2) * 12 : 0;
  const x = cx + offsetX;

  return (
    <g>
      {/* Win/loss halo */}
      <circle cx={x} cy={cy} r={r + 4} fill={haloColor} opacity={0.25} />
      <circle cx={x} cy={cy} r={r + 2} fill={haloColor} opacity={0.4} />
      {/* Ball body */}
      <circle cx={x} cy={cy} r={r} fill={ball.fill} stroke="#fff" strokeWidth={1.5} />
      {/* Number */}
      <text
        x={x}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fill={ball.text}
        fontSize={ball.label.length > 1 ? 7.5 : 8.5}
        fontWeight="bold"
        style={{ pointerEvents: 'none' }}
      >
        {ball.label}
      </text>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Custom dot renderers
// ---------------------------------------------------------------------------
function makeDotRenderer(
  dataKey: string,
  playerId: string,
  _color: string,
  showGameType: boolean,
) {
  return (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const value = payload?.[dataKey];
    if (value == null) return <g />;

    const isWinner = payload?.winnerId === playerId;
    const sameDayIndex = payload?.sameDayIndex ?? 0;
    const sameDayCount = payload?.sameDayCount ?? 1;

    if (showGameType && payload?.gameType) {
      return <PoolBallDot cx={cx} cy={cy} gameType={payload.gameType} isWinner={isWinner} sameDayIndex={sameDayIndex} sameDayCount={sameDayCount} />;
    }

    // Standard dot with same-day pixel offset
    const offsetX = sameDayCount > 1 ? (sameDayIndex - (sameDayCount - 1) / 2) * 10 : 0;

    return (
      <circle
        key={`dot-${dataKey}-${payload.date}-${sameDayIndex}`}
        cx={cx + offsetX}
        cy={cy}
        r={isWinner ? 6 : 4}
        fill={isWinner ? WIN_DOT : LOSS_DOT}
        stroke="#fff"
        strokeWidth={2}
      />
    );
  };
}

// Active dot: enlarged win/loss colored dot (replaces default hollow circle)
function makeActiveDotRenderer(playerId: string) {
  return (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return <g />;
    const isWinner = payload?.winnerId === playerId;
    const color = isWinner ? WIN_DOT : LOSS_DOT;
    return (
      <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2.5} />
    );
  };
}

// ---------------------------------------------------------------------------
// Custom Tooltip
// ---------------------------------------------------------------------------
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  playerAId: string;
  playerAName: string;
  playerBId: string;
  playerBName: string;
  showGameType?: boolean;
  allData: ChartRow[];
}

/** Render a single encounter block inside the tooltip. */
function EncounterBlock({
  row, playerAId, playerAName, playerBId, playerBName, showGameType, isFirst,
}: {
  row: ChartRow; playerAId: string; playerAName: string; playerBId: string; playerBName: string;
  showGameType?: boolean; isFirst: boolean;
}) {
  const aWon = row.winnerId === playerAId;
  const bWon = row.winnerId === playerBId;
  const ballInfo = BALL_COLORS[row.gameType];

  return (
    <div className={isFirst ? '' : 'mt-2 pt-2 border-t border-slate-100'}>
      {/* Game type badge (when multiple encounters on same date) */}
      {showGameType && ballInfo && (
        <div className="flex items-center gap-1.5 mb-0.5">
          <span
            className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[7px] font-bold"
            style={{ backgroundColor: ballInfo.fill, color: ballInfo.text }}
          >
            {ballInfo.label}
          </span>
          <span className="text-[10px] text-slate-400">{row.gameType}</span>
        </div>
      )}

      {/* Players + CSR inline + result */}
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: PLAYER_A_COLOR }} />
        <span className="text-slate-700 font-medium">{playerAName}</span>
        {row.playerA_csr != null && (
          <span className="text-slate-400 text-[11px]">({row.playerA_csr})</span>
        )}
        {aWon && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">WIN</span>}
        {bWon && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600">LOSS</span>}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: PLAYER_B_COLOR }} />
        <span className="text-slate-700 font-medium">{playerBName}</span>
        {row.playerB_csr != null && (
          <span className="text-slate-400 text-[11px]">({row.playerB_csr})</span>
        )}
        {bWon && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">WIN</span>}
        {aWon && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600">LOSS</span>}
      </div>

      {/* Stats — score and race only (CSR is now inline above) */}
      <div className="mt-1 space-y-0.5">
        {(row.playerA_gamesWon != null || row.playerB_gamesWon != null) && (
          <div className="flex justify-between">
            <span className="text-slate-500">Score</span>
            <span className="font-medium text-slate-700">
              {row.playerA_gamesWon ?? '?'} – {row.playerB_gamesWon ?? '?'}
            </span>
          </div>
        )}
        {(row.playerA_race != null || row.playerB_race != null) && (
          <div className="flex justify-between">
            <span className="text-slate-500">Race</span>
            <span className="font-medium text-slate-700">
              {row.playerA_race ?? '?'} to {row.playerB_race ?? '?'}
            </span>
          </div>
        )}
      </div>

      {/* Venue / division / makeup */}
      {(row.venue || row.divisionName || row.isMakeup) && (
        <div className="mt-1 text-[11px] text-slate-400 space-y-0.5">
          {row.venue && <div>{row.venue}</div>}
          {row.divisionName && <div>{row.divisionName}</div>}
          {row.isMakeup && (
            <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-600">
              Makeup
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CustomTooltipContent({
  active,
  payload,
  playerAId,
  playerAName,
  playerBId,
  playerBName,
  showGameType,
  allData,
  onDismiss,
}: CustomTooltipProps & { onDismiss?: () => void }) {
  if (!active || !payload?.length) return null;

  const firstRow = payload[0]?.payload as ChartRow | undefined;
  if (!firstRow) return null;

  // Find ALL encounters on this date (tooltip fires per x-axis, so we gather all)
  const dateRows = allData.filter((r) => r.date === firstRow.date);
  const rows = dateRows.length > 0 ? dateRows : [firstRow];

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        padding: '10px 14px',
        fontSize: '12px',
        lineHeight: 1.6,
        minWidth: 180,
        maxWidth: 280,
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-slate-700">{formatFullDate(firstRow.date)}</span>
        {onDismiss && (
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            onTouchEnd={(e) => { e.stopPropagation(); onDismiss(); }}
            className="ml-2 -mr-1 -mt-1 p-0.5 rounded text-slate-400 hover:text-slate-600 active:text-slate-800"
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="3" x2="11" y2="11" /><line x1="11" y1="3" x2="3" y2="11" />
            </svg>
          </button>
        )}
      </div>
      {rows.map((row, i) => (
        <EncounterBlock
          key={row.matchIndex}
          row={row}
          playerAId={playerAId}
          playerAName={playerAName}
          playerBId={playerBId}
          playerBName={playerBName}
          showGameType={showGameType || rows.length > 1}
          isFirst={i === 0}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------
interface HeadToHeadChartProps {
  encounters: HeadToHeadEncounter[];
  playerAId: string;
  playerAName: string;
  playerBId: string;
  playerBName: string;
  /** When true, render pool ball icons to distinguish 8/9/10-ball matches. */
  showGameType?: boolean;
  /** Callback when the user hovers over a chart point (match index) or leaves (null). */
  onHoveredIndex?: (index: number | null) => void;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const HeadToHeadChart: React.FC<HeadToHeadChartProps> = ({
  encounters,
  playerAId,
  playerAName,
  playerBId,
  playerBName,
  showGameType = false,
  onHoveredIndex,
}) => {
  const chartData = useMemo(() => buildChartData(encounters, showGameType), [encounters, showGameType]);
  const [lastHoveredIndex, setLastHoveredIndex] = useState<number | null>(null);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);

  // Which game types appear in the data (for "All" mode line rendering)
  const presentGameTypes = useMemo(() => {
    if (!showGameType) return [];
    const types = new Set(encounters.map((e) => e.game_type));
    return GAME_LINE_STYLES.filter((s) => types.has(s.gameType));
  }, [encounters, showGameType]);

  // Compute Y-axis domain from actual non-null values
  const yDomain = useMemo(() => {
    const values: number[] = [];
    for (const row of chartData) {
      if (row.playerA_csr != null) values.push(row.playerA_csr);
      if (row.playerB_csr != null) values.push(row.playerB_csr);
    }
    if (values.length === 0) return [0, 100] as [number, number];
    const min = Math.min(...values);
    const max = Math.max(...values);
    return [Math.max(0, min - 5), max + 5] as [number, number];
  }, [chartData]);

  const dotA = useMemo(
    () => makeDotRenderer('playerA_csr', playerAId, PLAYER_A_COLOR, showGameType),
    [playerAId, showGameType],
  );
  const dotB = useMemo(
    () => makeDotRenderer('playerB_csr', playerBId, PLAYER_B_COLOR, showGameType),
    [playerBId, showGameType],
  );
  const activeDotA = useMemo(() => makeActiveDotRenderer(playerAId), [playerAId]);
  const activeDotB = useMemo(() => makeActiveDotRenderer(playerBId), [playerBId]);

  const handleDismissTooltip = useCallback(() => {
    setTooltipDismissed(true);
    setLastHoveredIndex(null);
    onHoveredIndex?.(null);
  }, [onHoveredIndex]);

  const tooltipContent = useMemo(
    () =>
      (props: any) => {
        if (tooltipDismissed) return null;
        // Extract the hovered match index from tooltip payload and notify parent
        const row = props?.payload?.[0]?.payload as ChartRow | undefined;
        const hoveredIndex = (props?.active && row) ? row.matchIndex : null;
        if (hoveredIndex !== lastHoveredIndex) {
          setTimeout(() => {
            setLastHoveredIndex(hoveredIndex);
            onHoveredIndex?.(hoveredIndex);
          }, 0);
        }
        return (
          <CustomTooltipContent
            {...props}
            playerAId={playerAId}
            playerAName={playerAName}
            playerBId={playerBId}
            playerBName={playerBName}
            showGameType={showGameType}
            allData={chartData}
            onDismiss={handleDismissTooltip}
          />
        );
      },
    [playerAId, playerAName, playerBId, playerBName, lastHoveredIndex, onHoveredIndex, showGameType, chartData, tooltipDismissed, handleDismissTooltip],
  );

  const handleMouseLeave = useCallback(() => {
    setLastHoveredIndex(null);
    setTooltipDismissed(false);
    onHoveredIndex?.(null);
  }, [onHoveredIndex]);

  // Year bands & labels (mirrors CSR comparison chart)
  const dateKeys = useMemo(() => chartData.map((d) => d.date), [chartData]);
  const yearBands = useMemo(() => buildYearBackgroundBands(dateKeys), [dateKeys]);
  const yearSections = useMemo(() => buildYearSections(dateKeys), [dateKeys]);
  const yearLabelRenderer = useMemo(() => makeYearLabelRenderer(yearSections), [yearSections]);
  const dateLabelMap = useMemo(() => {
    const labels = buildChartDateLabels(dateKeys);
    return new Map(labels.map((l) => [l.date, l]));
  }, [dateKeys]);
  const axisTick = useMemo(() => makeDateAxisTickRenderer(dateLabelMap), [dateLabelMap]);
  const hasYearLabels = yearSections.length > 1;

  // Empty state
  if (!chartData.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-400 text-sm font-medium">No head-to-head matches found</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }} onMouseLeave={handleMouseLeave} onClick={() => setTooltipDismissed(false)}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />

        {yearBands.map((band) => (
          <ReferenceArea
            key={band.year}
            x1={band.startDate}
            x2={band.endDate}
            fill="#f8fafc"
            fillOpacity={0.9}
            strokeOpacity={0}
          />
        ))}

        <XAxis
          dataKey="date"
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          height={hasYearLabels ? 82 : 48}
          interval="preserveStartEnd"
          allowDuplicatedCategory={false}
        />

        <YAxis
          fontSize={11}
          stroke="#94a3b8"
          tickLine={false}
          axisLine={false}
          domain={yDomain}
          width={40}
        />

        <Tooltip content={tooltipContent} />

        {showGameType ? (
          /* "All" mode: one line pair per game type, each connecting only its own points */
          presentGameTypes.map((style) => (
            <React.Fragment key={style.gameType}>
              <Line
                type="monotone"
                dataKey={`a_${style.keyPrefix}_csr`}
                stroke={PLAYER_A_COLOR}
                strokeWidth={2}
                strokeDasharray={style.dash || undefined}
                dot={makeDotRenderer(`a_${style.keyPrefix}_csr` ,playerAId, PLAYER_A_COLOR, true)}
                activeDot={activeDotA}
                name={`${playerAName} (${style.label})`}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey={`b_${style.keyPrefix}_csr`}
                stroke={PLAYER_B_COLOR}
                strokeWidth={2}
                strokeDasharray={style.dash || undefined}
                dot={makeDotRenderer(`b_${style.keyPrefix}_csr` ,playerBId, PLAYER_B_COLOR, true)}
                activeDot={activeDotB}
                name={`${playerBName} (${style.label})`}
                connectNulls
              />
            </React.Fragment>
          ))
        ) : (
          /* Single game type: original two-line layout */
          <>
            {/* Shaded area between the two CSR lines */}
            <Area
              type="monotone"
              dataKey="playerA_csr"
              stroke="none"
              fill={AREA_FILL}
              fillOpacity={0.15}
              connectNulls
              activeDot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="playerA_csr"
              stroke={PLAYER_A_COLOR}
              strokeWidth={2.5}
              dot={dotA}
              activeDot={activeDotA}
              name={playerAName}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="playerB_csr"
              stroke={PLAYER_B_COLOR}
              strokeWidth={2.5}
              dot={dotB}
              activeDot={activeDotB}
              name={playerBName}
              connectNulls
            />
          </>
        )}

        {hasYearLabels && <Customized component={yearLabelRenderer} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default HeadToHeadChart;
